/**
 * @fileoverview ParentDropdown component for selecting parent items (e.g., resource groups, orgcharts).
 * Provides a combobox interface with search to select parent entities for hierarchical metadata.
 */

import { useTranslation } from "@/i18n";
import type { DropdownItem } from "../types";
import SearchableCombobox from "./SearchableCombobox";

/**
 * Props for the ParentDropdown component.
 */
interface ParentDropdownProps {
  /** Available parent items that can be selected */
  readonly parentItems: readonly DropdownItem[];
  /** Currently selected parent ID, if any */
  readonly selectedParentId: number | undefined;
  /** Label to display for the dropdown */
  readonly label: string;
  /** Whether the dropdown is currently visible */
  readonly isOpen: boolean;
  /** Whether the field is disabled (typically when linked to existing item) */
  readonly disabled: boolean;
  /** Callback to toggle dropdown visibility */
  readonly onToggle: () => void;
  /** Callback when a parent is selected */
  readonly onSelect: (parentId: number | undefined) => void;
}

/**
 * ParentDropdown allows users to select a parent item for hierarchical metadata with search capability.
 * Used for selecting resource groups, orgcharts, or parent requirements.
 *
 * @param props - Component props
 * @returns Parent combobox UI, or null if no parent items available
 */
export default function ParentDropdown({
  parentItems,
  selectedParentId,
  label,
  isOpen,
  disabled,
  onToggle,
  onSelect,
}: ParentDropdownProps) {
  const { t } = useTranslation();

  if (parentItems.length === 0) {
    return null;
  }

  const selectedParent = parentItems.find((p) => p.id === selectedParentId);

  return (
    <div className="space-y-2">
      <label className="text-sm text-[#18181b] leading-5">{label}</label>
      <div className="parent-dropdown-container">
        <SearchableCombobox
          items={parentItems}
          isOpen={isOpen}
          disabled={disabled}
          getItemName={(item) => item.name || "Unknown"}
          getItemKey={(item) => item.id}
          renderTrigger={() => (
            <span>
              {selectedParent?.name || t("documentReview.modal.selectParent")}
            </span>
          )}
          onSelect={(item) => onSelect(item.id)}
          onToggle={onToggle}
          showClearOption
          clearOptionLabel={t("documentReview.modal.noSelection")}
          onClear={() => onSelect(undefined)}
          searchPlaceholder={t("common.searching")}
          noResultsMessage={t("documentReview.modal.noResults")}
          isPlaceholder={!selectedParentId}
        />
      </div>
    </div>
  );
}
